import React, { useState } from 'react';
import { Monitor, Smartphone, Tablet, Wifi, WifiOff, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import { useUX } from '../contexts/UXContext';

/**
 * Device Indicator Component
 * Shows current device context and UX negotiation status
 */

const DeviceIndicator = () => {
  const { 
    deviceContext, 
    uxFormat, 
    isNegotiating,
    isMobile,
    isTablet,
    isDesktop,
    isSlowNetwork,
    negotiateUX
  } = useUX();

  const [isExpanded, setIsExpanded] = useState(false);

  if (!deviceContext) {
    return null;
  }

  const getDeviceIcon = () => {
    if (isMobile) return <Smartphone className="w-4 h-4" />;
    if (isTablet) return <Tablet className="w-4 h-4" />;
    return <Monitor className="w-4 h-4" />;
  };

  const getNetworkIcon = () => {
    if (isSlowNetwork) return <WifiOff className="w-4 h-4 text-red-500" />;
    return <Wifi className="w-4 h-4 text-green-500" />;
  };

  const getBandwidthColor = (bandwidth) => {
    if (['2g', 'slow-2g'].includes(bandwidth)) return 'text-red-600';
    if (bandwidth === '3g') return 'text-yellow-600';
    if (bandwidth === '4g') return 'text-green-600';
    return 'text-blue-600';
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      {/* Compact View */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden">
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-4 py-2 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
        >
          <div className="flex items-center space-x-2">
            {getDeviceIcon()}
            {getNetworkIcon()}
          </div>
          <span className="text-sm font-medium text-gray-700">
            {deviceContext.device}
          </span>
          {isNegotiating && (
            <RefreshCw className="w-3 h-3 animate-spin text-indigo-600" />
          )}
          {isExpanded ? (
            <ChevronDown className="w-4 h-4 ml-auto text-gray-400" />
          ) : (
            <ChevronUp className="w-4 h-4 ml-auto text-gray-400" />
          )}
        </button>

        {/* Expanded View */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-4 space-y-3">
            {/* Device Info */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Device Context
              </h4>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Type:</span>
                  <span className="font-medium text-gray-900">{deviceContext.device}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Screen:</span>
                  <span className="font-medium text-gray-900">
                    {deviceContext.screenWidth} × {deviceContext.screenHeight}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Network:</span>
                  <span className={`font-medium ${getBandwidthColor(deviceContext.bandwidth)}`}>
                    {deviceContext.bandwidth.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Orientation:</span>
                  <span className="font-medium text-gray-900 capitalize">
                    {deviceContext.orientation}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Touch:</span>
                  <span className="font-medium text-gray-900">
                    {deviceContext.hasTouch ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
            </div>

            {/* UX Format */}
            {uxFormat && (
              <div className="pt-3 border-t border-gray-200">
                <h4 className="text-xs font-semibold text-gray-500 uppercase mb-2">
                  Negotiated Format
                </h4>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Layout:</span>
                    <span className="font-medium text-indigo-600 capitalize">
                      {uxFormat.layout || uxFormat.visualizationType}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Data:</span>
                    <span className="font-medium text-gray-900 capitalize">
                      {uxFormat.data_granularity || uxFormat.dataGranularity}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Items/Page:</span>
                    <span className="font-medium text-gray-900">
                      {uxFormat.items_per_page || uxFormat.maxItemsPerPage}
                    </span>
                  </div>
                  {uxFormat.reasoning && (
                    <div className="pt-2 mt-2 border-t border-gray-100">
                      <p className="text-xs text-gray-600 italic">
                        "{uxFormat.reasoning}"
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Re-negotiate Button */}
            <button
              onClick={() => negotiateUX(true)}
              disabled={isNegotiating}
              className="w-full mt-3 py-2 px-4 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center justify-center space-x-2 text-sm font-medium"
            >
              <RefreshCw className={`w-4 h-4 ${isNegotiating ? 'animate-spin' : ''}`} />
              <span>{isNegotiating ? 'Negotiating...' : 'Re-negotiate UX'}</span>
            </button>

            {/* Info */}
            <p className="text-xs text-gray-500 text-center pt-2 border-t border-gray-200">
              UX automatically adapts to device
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DeviceIndicator;

